<script setup lang="ts">
import { computed, getCurrentInstance } from "vue";

/* If we need label positioning let me know */
const props = defineProps<{
  label?: string;
  modelValue?: boolean;
  value?: boolean;
  size: "xl" | "lg" | "md" | "sm" | (string & {});
  color:
    | "positive-parent"
    | "warning-parent"
    | "dange-parent"
    | "disabled-parent"
    | "positive-child"
    | "warning-child"
    | "dange-child"
    | "disabled-child"
    | "input"
    | (string & {});
  customClass?: string;
  required?: boolean;
  /**
   * Accessible name when the visible text lives outside this component (same contract as
   * Switch's ariaLabel). Declared as a real prop so callers don't depend on fallthrough-attr
   * precedence beating the internal :aria-label fallback.
   */
  ariaLabel?: string;
}>();

// reason: Vue 3 Boolean prop casting coerces unset boolean props to `false`, so
// `props.modelValue !== undefined` can never identify whether the parent actually
// passed `modelValue` (vs the legacy `:value` API). Inspect `vnode.props` to detect
// the parent's real binding intent so consumers using `:value` keep working.
const instance = getCurrentInstance();
const hasModelValueBinding = computed(() => {
  const vnodeProps = instance?.vnode.props;
  return !!vnodeProps && ("modelValue" in vnodeProps || "model-value" in vnodeProps);
});
// modelValue is the primary v-model prop; value is kept as a backwards-compatible alias
const effectiveValue = computed(() => (hasModelValueBinding.value ? props.modelValue ?? false : props.value ?? false));

const size = computed(() => {
  if (props.size === "xl") return "w-5 h-5";
  else if (props.size === "lg") return "w-4 h-4";
  else if (props.size === "md") return "w-3 h-3";
  else if (props.size === "sm") return "w-2 h-2";
  else return props.size;
});
const borderColor = computed(() => {
  if (props.color === "positive-parent") return "border-app-green-500";
  else if (props.color === "warning-parent") return "border-app-yellow-400";
  else if (props.color === "dange-parent") return "border-app-red-500";
  else if (props.color === "disabled-parent") return "border-slate-300";
  else if (props.color === "positive-child") return "border-app-green-300";
  else if (props.color === "warning-child") return "border-app-yellow-300";
  else if (props.color === "dange-child") return "border-app-red-400";
  else if (props.color === "disabled-child") return "border-slate-300";
  else if (props.color === "input") return "border-slate-200";
  else return props.color;
});

const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
  (e: "update:value", v: boolean): void;
}>();

const toggle = () => {
  const next = !effectiveValue.value;
  emit("update:modelValue", next);
  emit("update:value", next);
};
</script>

<template>
  <div
    role="checkbox"
    :aria-checked="effectiveValue"
    :aria-required="required ? true : undefined"
    :aria-label="ariaLabel || label || 'Toggle'"
    tabindex="0"
    class="focus-visible:ring-app-blue-600 rounded-4 flex cursor-pointer items-center gap-2 focus:outline-none focus-visible:ring-2"
    @click="toggle"
    @keydown.space.prevent="toggle"
    @keydown.enter.prevent="toggle">
    <span
      v-if="label"
      class="text-xs font-semibold select-none"
      >{{ label }}</span
    >
    <div
      :class="[size, borderColor, customClass]"
      class="rounded-4 bg-slate-0 flex items-center justify-center border shadow-xs"
      @click.stop="toggle">
      <font-awesome-icon
        v-if="effectiveValue"
        icon="fa-solid fa-check"
        class="text-type-default text-[0.6rem]" />
    </div>
  </div>
</template>
